<template>
  <div class="signup-container">
    <el-card class="signup-card">
      <div class="logo-container" @click="goToHomePage">
        <img :src="branding.logo" :alt="branding.logoAlt" class="logo">
        <div class="brand-text">
          <span class="brand-name">{{ branding.displayName }}</span>
          <span v-if="branding.subtitle" class="brand-sub">{{ branding.subtitle }}</span>
        </div>
      </div>
      <template v-if="step === 'signup'">
        <el-form :model="signupForm" :rules="rules" ref="signupForm">
          <el-form-item label="User Email" prop="user_email">
            <el-input v-model="signupForm.user_email" placeholder="Enter your email"></el-input>
          </el-form-item>
          <el-form-item label="User Name" prop="user_name">
            <el-input v-model="signupForm.user_name" placeholder="Enter your name"></el-input>
          </el-form-item>
          <el-form-item label="User Type" prop="user_type">
            <el-radio-group v-model="signupForm.user_type">
              <el-radio label="individual">Individual</el-radio>
              <el-radio label="institution">Institution</el-radio>
            </el-radio-group>
          </el-form-item>
          <div class="turnstile-container" v-if="!isDevMode">
            <div ref="turnstileWidget"></div>
          </div>
          <el-form-item>
            <el-button type="primary" @click="submitForm('signupForm')" style="width: 100%" :disabled="!isDevMode && !turnstileToken">Sign Up</el-button>
          </el-form-item>
        </el-form>
        <div class="login-note">
          <span>Already have an account? </span>
          <router-link :to="loginLink" class="login-link">Login</router-link>
        </div>
      </template>
      <template v-else-if="step === 'otp'">
        <div class="otp-header">
          <el-button type="text" icon="el-icon-arrow-left" class="otp-back" @click="step = 'signup'">Back</el-button>
        </div>
        <div class="otp-instructions">An OTP code has been sent to your email. Please enter it below to complete registration.</div>
        <el-form :model="otpForm" :rules="otpRules" ref="otpForm">
          <el-form-item label="OTP Code" prop="otp_code">
            <el-input v-model="otpForm.otp_code" maxlength="6" placeholder="Enter OTP code"></el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="submitOtp('otpForm')" style="width: 100%">Verify & Register</el-button>
          </el-form-item>
        </el-form>
      </template>
    </el-card>
  </div>
</template>

<script>
import { postRequest } from '@/apis/front'
import branding from '@/config/branding'

export default {
  name: 'Signup',
  data() {
    return {
      branding,
      step: 'signup',
      turnstileToken: '',
      turnstileWidgetId: null,
      signupForm: {
        user_name: '',
        user_email: '',
        user_type: ''
      },
      rules: {
        user_name: [
          { required: true, message: 'Please enter your name', trigger: 'blur' }
        ],
        user_type: [
          { required: true, message: 'Please select a user type', trigger: 'change' }
        ],
        user_email: [
          { required: true, message: 'Please enter your email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email', trigger: ['blur', 'change'] }
        ]
      },
      otpForm: {
        otp_code: ''
      },
      otpRules: {
        otp_code: [
          { required: true, message: 'Please enter the OTP code', trigger: 'blur' },
          { min: 4, max: 6, message: 'OTP code should be 4-6 digits', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    if (!this.isDevMode) {
      this.renderTurnstile()
    }
  },
  beforeDestroy() {
    if (this.turnstileWidgetId !== null && window.turnstile) {
      window.turnstile.remove(this.turnstileWidgetId)
    }
  },
  computed: {
    isDevMode() {
      return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    },
    loginLink() {
      // Pass redirect parameter back to login page if present
      const redirect = this.$route.query.redirect
      if (redirect) {
        return { path: '/login', query: { redirect } }
      }
      return '/login'
    }
  },
  methods: {
    goToHomePage() {
      window.location.href = 'https://knowit.today';
    },
    renderTurnstile() {
      if (!document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      const tryRender = () => {
        if (window.turnstile && this.$refs.turnstileWidget) {
          this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileWidget, {
            sitekey: '0x4AAAAAACnvaf1-Ku2pDQ-n',
            theme: 'light',
            size: 'flexible',
            callback: (token) => { this.turnstileToken = token },
            'expired-callback': () => { this.turnstileToken = '' },
            'error-callback': () => { this.turnstileToken = '' }
          })
        } else {
          setTimeout(tryRender, 200)
        }
      }
      tryRender()
    },
    resetTurnstile() {
      this.turnstileToken = ''
      if (this.turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.reset(this.turnstileWidgetId)
      }
    },
    async submitForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          if (!this.isDevMode && !this.turnstileToken) {
            this.$message.error('Please complete the verification');
            return;
          }
          // 1. Verify email availability
          const resp = await postRequest('verifyEmailAvailability', {
            student_email: this.signupForm.user_email,
            turnstile_token: this.turnstileToken || undefined
          });
          this.resetTurnstile();
          if (resp.data.code !== 0) {
            this.$message.error('This email is already registered. Please directly login or change to another email.');
            return;
          } else {
            // Switch to OTP input interface
            this.step = 'otp';
            return;
          }
        }
      });
    },
    async submitOtp(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          // Call backend to verify OTP and complete registration
          const resp = await postRequest('signupByEmail', {
            student_email: this.signupForm.user_email,
            opt_code: this.otpForm.otp_code,
            user_name: this.signupForm.user_name,
            user_type: this.signupForm.user_type,

          });
          if (resp && resp.data && resp.data.code === 0) {


            this.$message.success('Sign up successful! You will automatically login.');

            this.$store.dispatch('loginByEmail', {
              student_email: this.signupForm.user_email,
              opt_code: this.otpForm.otp_code
            }).then((res) => {
              if (res == null) {
                this.$message.success('Login successful')

                // Mark onboarding as completed since user provided name during signup
                localStorage.setItem('onboardingCompleted', 'true')

                // Check for redirect URL from query parameter
                const redirectUrl = this.$route.query.redirect
                if (redirectUrl) {
                  this.$router.push(redirectUrl)
                } else {
                  this.$router.push('/plaza')
                }
              } else {
                this.$message.success(res)
              }
            })
          } else if (resp && resp.data && resp.data.code === -2) {
            this.$message.error('OTP error. Please check the code and try again.');
          } else {
            this.$message.error((resp && resp.data && resp.data.msg) || 'Registration failed. Please try again.');
          }
        }
      });
    }
  }
}
</script>

<style scoped lang="scss">
.signup-container {
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f2f5;
}
.signup-card {
  width: 350px;
}
.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  cursor: pointer;
}
.logo {
  height: 40px;
  margin-right: 10px;
}
.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.brand-name {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0.5px;
}
.brand-sub {
  font-size: 14px;
  font-weight: 500;
  color: #909399;
  letter-spacing: 0.5px;
}
.turnstile-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}
.otp-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.otp-back {
  margin-left: -8px;
  color: #409EFF;
  font-size: 16px;
  font-weight: 500;
}
.otp-instructions {
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
  text-align: center;
}
.login-note {
  text-align: center;
  margin-top: 18px;
  font-size: 14px;
  color: #666;
}
.login-link {
  color: #1159AF;
  font-weight: 500;
  text-decoration: underline;
  margin-left: 3px;
}
.login-link:hover {
  color: #409EFF;
}
</style>
